@if (loading) {
  <div class="flex flex-col gap-4">
    <div class="h-16 animate-pulse rounded-lg bg-slate-200/70"></div>
    <div class="h-44 animate-pulse rounded-lg bg-slate-200/70"></div>
  </div>
} @else if (!cabinets.length) {
  <section class="flex flex-col items-center gap-3 rounded-lg border !border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
    <div class="grid h-14 w-14 place-items-center rounded-2xl bg-red-50 text-red-600">
      <app-icon class="!h-7 !w-7 !text-[28px]">local_shipping</app-icon>
    </div>
    <h3 class="!m-0 !text-base !font-semibold text-slate-900">{{ 'novaposhta.shipments.no_cabinet_title' | translate }}</h3>
    <p class="!m-0 max-w-md text-sm text-slate-500">{{ 'novaposhta.shipments.no_cabinet_text' | translate }}</p>
    <app-button [customClass]="'!h-9 !rounded-lg'" (onChangeClick)="openSettings()">
      <span class="flex items-center gap-1 text-sm font-semibold leading-none">
        <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">key</app-icon>
        {{ 'novaposhta.settings.add' | translate }}
      </span>
    </app-button>
  </section>
} @else {
  <div class="flex flex-col gap-4">
    <section class="flex items-center gap-3 rounded-lg border !border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div class="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-red-50 text-red-600">
        <app-icon class="!h-5 !w-5 !text-[20px]">local_shipping</app-icon>
      </div>
      <h3 class="!m-0 !text-sm !font-semibold !leading-5 text-slate-900">{{ 'novaposhta.shipments.title' | translate }}</h3>
      <span class="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">{{ shipments.length }}</span>
      <div class="ml-auto flex items-center gap-1">
        <app-button
          btnType="icon"
          [ariaLabel]="'novaposhta.shipments.settings' | translate"
          [matTooltip]="'novaposhta.shipments.settings' | translate"
          (onChangeClick)="openSettings()"
        >
          <app-icon class="text-slate-400">settings</app-icon>
        </app-button>
        <app-button btnType="stroked" [customClass]="'!h-9'" (onChangeClick)="openLinkForm()">
          <span class="flex items-center gap-1 text-sm font-semibold leading-none">
            <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">add_link</app-icon>
            {{ 'novaposhta.shipments.link' | translate }}
          </span>
        </app-button>
        <app-button [customClass]="'!h-9'" (onChangeClick)="openForm()">
          <span class="flex items-center gap-1 text-sm font-semibold leading-none">
            <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">add</app-icon>
            {{ 'novaposhta.shipments.create' | translate }}
          </span>
        </app-button>
      </div>
    </section>

    @for (shipment of shipments; track shipment.id) {
      <article
        class="overflow-hidden rounded-lg border !border-slate-200 bg-white shadow-sm transition"
        [class.opacity-60]="busyId === shipment.id"
      >
        <div class="flex flex-wrap items-center gap-x-3 gap-y-2 border-b !border-slate-100 px-4 py-3">
          <div class="flex items-center gap-1">
            <span class="font-mono text-base font-bold tracking-wide text-slate-900">{{ formatTtn(shipment.ttn_number) }}</span>
            <app-button
              btnType="icon"
              [ariaLabel]="'novaposhta.shipments.copy' | translate"
              [matTooltip]="(copiedId === shipment.id ? 'novaposhta.shipments.copied' : 'novaposhta.shipments.copy') | translate"
              (onChangeClick)="copy(shipment)"
            >
              <app-icon class="!h-4 !w-4 !text-[16px] text-slate-400">{{ copiedId === shipment.id ? 'check' : 'content_copy' }}</app-icon>
            </app-button>
          </div>
          <span
            class="flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold"
            [ngClass]="statusStyles[shipment.status].badge"
            [matTooltip]="shipment.status_text"
          >
            <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">{{ statusStyles[shipment.status].icon }}</app-icon>
            {{ 'novaposhta.status.' + shipment.status | translate }}
          </span>
          @if (shipment.source === 'linked') {
            <span
              class="flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700"
              [matTooltip]="'novaposhta.shipments.linked_hint' | translate"
            >
              <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">link</app-icon>
              {{ 'novaposhta.shipments.linked' | translate }}
            </span>
          }

          <div class="ml-auto flex items-center">
            <a
              class="inline-flex h-9 items-center gap-1 rounded-lg px-2 text-sm font-medium text-slate-600 no-underline transition hover:bg-slate-100 hover:text-slate-900"
              target="_blank"
              rel="noopener"
              [href]="trackingUrl(shipment)"
            >
              <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">travel_explore</app-icon>
              {{ 'novaposhta.shipments.track' | translate }}
            </a>
            <app-button
              btnType="icon"
              [ariaLabel]="'novaposhta.shipments.print' | translate"
              [matTooltip]="'novaposhta.shipments.print' | translate"
              [matMenuTriggerFor]="printMenu"
            >
              <app-icon class="text-slate-500">print</app-icon>
            </app-button>
            <mat-menu #printMenu="matMenu" xPosition="before">
              @for (format of printFormats; track format.value) {
                <app-button btnType="no-btn" [customClass]="'flex cursor-pointer items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50'" (onChangeClick)="print(shipment, format.value)">
                  <app-icon class="!h-4 !w-4 !text-[16px] text-slate-400">{{ format.icon }}</app-icon>
                  {{ format.label | translate }}
                </app-button>
              }
            </mat-menu>
            <app-button
              btnType="icon"
              [ariaLabel]="'novaposhta.shipments.unlink' | translate"
              [matTooltip]="'novaposhta.shipments.unlink' | translate"
              [disabled]="busyId === shipment.id"
              (onChangeClick)="unlink(shipment)"
            >
              <app-icon class="text-slate-500">link_off</app-icon>
            </app-button>
            @if (shipment.source !== 'linked' && shipment.status === 'created') {
              <app-button
                btnType="icon"
                [ariaLabel]="'delete' | translate"
                [matTooltip]="'novaposhta.shipments.delete' | translate"
                [disabled]="busyId === shipment.id"
                (onChangeClick)="remove(shipment)"
              >
                <app-icon class="text-rose-500">delete_outline</app-icon>
              </app-button>
            }
          </div>
        </div>

        <dl class="!m-0 grid grid-cols-1 gap-x-6 gap-y-3 px-4 py-3 text-sm md:grid-cols-2">
          <div class="flex min-w-0 flex-col gap-0.5">
            <dt class="text-xs font-medium text-slate-400">{{ 'novaposhta.form.recipient' | translate }}</dt>
            <dd class="!m-0 truncate text-slate-800">
              <span class="font-medium">{{ shipment.recipient.name }}</span>
              <span class="text-slate-500"> · {{ formatNpPhone(shipment.recipient.phone) }}</span>
            </dd>
          </div>
          <div class="flex min-w-0 flex-col gap-0.5">
            <dt class="text-xs font-medium text-slate-400">{{ 'novaposhta.delivery_type.' + shipment.recipient.delivery_type | translate }}</dt>
            <dd class="!m-0 truncate text-slate-800" [matTooltip]="shipment.recipient.address">
              <span class="font-medium">{{ shipment.recipient.city_name }}</span>, {{ shipment.recipient.address }}
            </dd>
          </div>
          <div class="flex min-w-0 flex-col gap-0.5">
            <dt class="text-xs font-medium text-slate-400">{{ 'novaposhta.form.cargo' | translate }}</dt>
            <dd class="!m-0 flex min-w-0 items-center gap-1.5 text-slate-800">
              <app-icon class="!h-4 !w-4 shrink-0 !text-[16px] text-slate-400">{{ cargoIcons[shipment.cargo.type] }}</app-icon>
              <span class="truncate">
                {{ 'novaposhta.cargo_type.' + shipment.cargo.type | translate }} ·
                {{ 'novaposhta.shipments.weight_seats' | translate: { weight: shipment.cargo.weight, seats: shipment.cargo.seats } }}
                @if (shipment.cargo.description) {
                  <span class="text-slate-500"> · {{ shipment.cargo.description }}</span>
                }
              </span>
            </dd>
          </div>
          <div class="flex min-w-0 flex-col gap-0.5">
            <dt class="text-xs font-medium text-slate-400">{{ 'novaposhta.form.declared_cost' | translate }}</dt>
            <dd class="!m-0 text-slate-800">{{ formatMoneyAmount(shipment.cargo.declared_cost, 'UAH') }}</dd>
          </div>
          @if (shipment.cod.type !== 'none') {
            <div class="flex min-w-0 flex-col gap-0.5">
              <dt class="text-xs font-medium text-slate-400">{{ 'novaposhta.cod_type.' + shipment.cod.type | translate }}</dt>
              <dd class="!m-0 font-semibold text-slate-900">{{ formatMoneyAmount(shipment.cod.amount, 'UAH') }}</dd>
            </div>
          }
          <div class="flex min-w-0 flex-col gap-0.5">
            <dt class="text-xs font-medium text-slate-400">{{ 'novaposhta.form.delivery_cost' | translate }}</dt>
            <dd class="!m-0 text-slate-800">
              {{ formatMoneyAmount(shipment.delivery_cost, 'UAH') }}
              @if (shipment.payer) {
                <span class="text-slate-500">· {{ 'novaposhta.shipments.paid_by.' + shipment.payer | translate }}</span>
              }
            </dd>
          </div>
          @if (shipment.estimated_delivery_date) {
            <div class="flex min-w-0 flex-col gap-0.5">
              <dt class="text-xs font-medium text-slate-400">{{ 'novaposhta.form.delivery_date' | translate }}</dt>
              <dd class="!m-0 text-slate-800">{{ shipment.estimated_delivery_date | date: 'dd.MM.yyyy' }}</dd>
            </div>
          }
        </dl>

        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 border-t !border-slate-100 bg-slate-50/70 px-4 py-2 text-xs text-slate-500">
          @if (shipment.cabinet.name) {
            <span class="flex items-center gap-1">
              <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">key</app-icon>
              {{ shipment.cabinet.name }}
            </span>
          }
          <span>{{ 'novaposhta.shipments.created_at' | translate: { date: (shipment.created_at | date: 'dd.MM.yyyy HH:mm') } }}</span>
          @if ($first) {
            <span class="ml-auto flex items-center gap-1 text-emerald-700">
              <app-icon class="!h-3.5 !w-3.5 !text-[14px] !leading-none">sync_alt</app-icon>
              {{ 'novaposhta.shipments.in_field' | translate }}
            </span>
          }
        </div>
      </article>
    } @empty {
      <section class="flex flex-col items-center gap-3 rounded-lg border border-dashed !border-slate-300 bg-white px-6 py-12 text-center">
        <div class="grid h-12 w-12 place-items-center rounded-full bg-slate-100 text-slate-400">
          <app-icon>package_2</app-icon>
        </div>
        <span class="text-sm font-semibold text-slate-900">{{ 'novaposhta.shipments.empty_title' | translate }}</span>
        <span class="max-w-md text-sm text-slate-500">{{ 'novaposhta.shipments.empty_text' | translate }}</span>
        <div class="flex flex-wrap justify-center gap-2">
          <app-button btnType="stroked" [customClass]="'!h-9 !rounded-lg'" (onChangeClick)="openLinkForm()">
            <span class="flex items-center gap-1 text-sm font-semibold leading-none">
              <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">add_link</app-icon>
              {{ 'novaposhta.shipments.link' | translate }}
            </span>
          </app-button>
          <app-button [customClass]="'!h-9 !rounded-lg'" (onChangeClick)="openForm()">
            <span class="flex items-center gap-1 text-sm font-semibold leading-none">
              <app-icon class="!h-4 !w-4 !text-[16px] !leading-none">add</app-icon>
              {{ 'novaposhta.shipments.create' | translate }}
            </span>
          </app-button>
        </div>
      </section>
    }
  </div>
}
